import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Phone, ChevronDown, ChevronRight, LayoutGrid, Check, Bath, ChefHat, Sun, Building2, Hammer, Ruler, Sparkles } from "lucide-react";
import { CONTACT } from "@/data/content";
import { useSiteContent } from "@/context/ContentContext";
import { useQuote } from "@/context/QuoteContext";
import { trackCall } from "@/lib/analytics";

const TOP_LINKS = [
  { label: "About", to: "/about", testid: "nav-link-about" },
  { label: "Areas", to: "/areas", testid: "nav-link-areas" },
  { label: "Process", href: "#process", testid: "nav-link-process" },
  { label: "Guide", to: "/guide", testid: "nav-link-guide" },
  { label: "Contact", to: "/contact", testid: "nav-link-contact" },
];

const BADGES = [
  { label: "Servicing Perth Metro", dot: true },
  { label: "Obligation-Free Quotes" },
  { label: "Fully Insured" },
  { label: "Rectified Specialists" },
];

const QUICK_SERVICES = ["bathroom", "kitchen", "outdoor", "commercial"];

const SERVICE_ICONS = {
  bathroom: Bath,
  kitchen: ChefHat,
  outdoor: Sun,
  commercial: Building2,
  "feature-walls": LayoutGrid,
  "tile-removal": Hammer,
  "rectified-tiling": Ruler,
  "bathroom-renovations": Sparkles,
};

const MOBILE_TABS = [
  { label: "About", to: "/about" },
  { label: "Areas", to: "/areas" },
  { label: "Process", href: "#process" },
  { label: "Gallery", href: "#gallery" },
  { label: "Guide", to: "/guide" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const { categories: CATEGORIES } = useSiteContent();
  const { openQuote } = useQuote();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const quickLinks = QUICK_SERVICES.map((id) => CATEGORIES.find((c) => c.id === id)).filter(Boolean);

  // Shrink to a single row once the visitor starts scrolling (Smartfix-style).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#e2e8f0] shadow-[0_2px_20px_-10px_rgba(15,23,42,0.15)]"
    >
      {/* Mobile status bar — thin dark strip like Smartfix */}
      <div className="lg:hidden bg-[#0b1120] text-white/90 text-center py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em]">
        <span className="inline-flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#9c7a4d]" aria-hidden />
          Servicing Perth Metro · Free Quotes
        </span>
      </div>

      {/* Row 1 — logo, page links, trust badges (desktop; collapses on scroll) */}
      <div
        className={`hidden lg:block overflow-hidden transition-all duration-500 ${
          scrolled ? "max-h-0 opacity-0 border-b border-transparent" : "max-h-14 opacity-100 border-b border-[#e2e8f0]"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-2.5 flex items-center justify-between gap-8">
          <a href="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img src="/brand/logo.png" alt="Perth Tiling WA" decoding="async" className="h-10 w-auto object-contain" />
          </a>
          <nav className="flex items-center gap-7">
            {TOP_LINKS.map((n) =>
              n.to ? (
                <Link key={n.label} to={n.to} data-testid={n.testid} className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#475569] hover:text-[#9c7a4d] transition-colors">
                  {n.label}
                </Link>
              ) : (
                <a key={n.label} href={n.href} data-testid={n.testid} className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#475569] hover:text-[#9c7a4d] transition-colors">
                  {n.label}
                </a>
              )
            )}
          </nav>
          <div className="flex items-center gap-5 shrink-0">
            {BADGES.map((b) => (
              <span key={b.label} className="flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[#0f172a]">
                {b.dot ? (
                  <span className="h-2 w-2 rounded-full bg-[#9c7a4d]" aria-hidden />
                ) : (
                  <Check className="h-3.5 w-3.5 text-[#9c7a4d]" strokeWidth={3} />
                )}
                {b.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2 — all services pill, quick service links, phone, CTA (slims on scroll) */}
      <div className={`mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between gap-6 transition-[padding] duration-500 ${scrolled ? "py-1.5" : "py-2.5"}`}>
        {/* Mobile logo + phone row (Smartfix layout) */}
        <a href="/" className="flex lg:hidden items-center shrink-0">
          <img src="/brand/logo.png" alt="Perth Tiling WA" decoding="async" className="h-9 w-auto object-contain" />
        </a>

        {/* Mobile: big tap-to-call number instead of pills */}
        <div className="flex lg:hidden items-center gap-3 shrink-0 ml-auto">
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            onClick={() => trackCall("navbar_mobile")}
            data-testid="nav-mobile-call-btn"
            className="flex items-center gap-2.5 group"
          >
            <Phone className="h-6 w-6 text-[#9c7a4d]" />
            <span className="font-serif text-xl sm:text-2xl font-bold text-[#0f172a] whitespace-nowrap">{CONTACT.phone}</span>
          </a>
        </div>
      </div>

      {/* Mobile action row — MENU / HIDE MENU + FREE QUOTE, always visible (Smartfix) */}
      <div className="lg:hidden border-t border-[#e2e8f0] px-4 py-2.5 grid grid-cols-2 gap-3">
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-[0.72rem] font-bold uppercase tracking-[0.16em] transition-colors ${
            open ? "bg-[#f1f5f9] text-[#0f172a]" : "border border-[#cbd5e1] text-[#0f172a]"
          }`}
        >
          {open ? <><X className="h-4 w-4 text-[#9c7a4d]" /> Hide Menu</> : <><LayoutGrid className="h-4 w-4 text-[#9c7a4d]" /> Menu</>}
        </button>
        <button
          data-testid="nav-header-quote-btn"
          onClick={openQuote}
          className="inline-flex items-center justify-center bg-[#9c7a4d] text-white active:bg-[#0f172a] rounded-full px-4 py-3 text-[0.72rem] font-bold uppercase tracking-[0.16em] transition-colors"
        >
          Get Free Quote
        </button>
      </div>

      {/* Mobile tab row — visible when menu open (horizontal scroll like Smartfix) */}
      {open && (
        <div data-testid="nav-mobile-tabs" data-lenis-prevent className="lg:hidden flex overflow-x-auto border-t border-b border-[#e2e8f0] divide-x divide-[#e2e8f0] bg-white">
          {MOBILE_TABS.map((t) =>
            t.to ? (
              <Link key={t.label} to={t.to} onClick={() => setOpen(false)} className="px-6 py-3.5 text-sm font-medium text-[#0f172a] whitespace-nowrap shrink-0">
                {t.label}
              </Link>
            ) : (
              <a key={t.label} href={t.href} onClick={() => setOpen(false)} className="px-6 py-3.5 text-sm font-medium text-[#0f172a] whitespace-nowrap shrink-0">
                {t.label}
              </a>
            )
          )}
        </div>
      )}

      {/* Desktop row 2 — services pill, quick links, phone, CTA */}
      <div className="hidden lg:block">
        <div className={`mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between gap-6 transition-[padding] duration-500 ${scrolled ? "py-1.5" : "py-2.5"}`}>
          <div className="flex items-center gap-7 min-w-0">
            {/* All Services dropdown — standout gold pill */}
          <div className="relative group shrink-0" data-testid="nav-services">
            <button className="flex items-center gap-2 bg-[#9c7a4d] text-white rounded-full px-6 py-2.5 text-xs font-bold uppercase tracking-[0.16em] hover:bg-[#b8935c] transition-colors duration-300">
              <LayoutGrid className="h-4 w-4" /> All Services <ChevronDown className="h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
            </button>
            <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
              <div className="w-80 bg-[#0b1120] border border-white/10 shadow-2xl rounded-xl overflow-hidden p-2">
                {CATEGORIES.map((c) => {
                  const Icon = SERVICE_ICONS[c.id] || LayoutGrid;
                  return (
                    <Link
                      key={c.id}
                      to={`/services/${c.id}`}
                      data-testid={`nav-service-${c.id}`}
                      className="flex items-center gap-3 px-4 py-3 text-[#e2e8f0] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <Icon className="h-4 w-4 text-[#c9a87c] shrink-0" strokeWidth={1.8} />
                      <span>
                        <span className="block font-semibold text-sm">{c.title}</span>
                        <span className="block text-[0.62rem] uppercase tracking-[0.16em] text-[#94a3b8] mt-0.5">{c.tag}</span>
                      </span>
                    </Link>
                  );
                })}
                <Link
                  to="/services"
                  data-testid="nav-service-all"
                  className="block px-4 py-3 mt-1 border-t border-white/10 text-[#c9a87c] hover:text-white transition-colors"
                >
                  <span className="text-xs font-bold uppercase tracking-[0.18em]">View All Services</span>
                </Link>
              </div>
            </div>
          </div>

          {quickLinks.map((c) => (
            <Link
              key={c.id}
              to={`/services/${c.id}`}
              data-testid={`nav-quick-${c.id}`}
              className="text-sm font-semibold text-[#0f172a] hover:text-[#9c7a4d] transition-colors whitespace-nowrap"
            >
              {c.title}
            </Link>
          ))}
        </div>

          <div className="flex items-center gap-5 shrink-0">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              onClick={() => trackCall("navbar_desktop")}
              data-testid="nav-phone"
              className="flex items-center gap-2.5 group"
            >
              <span className="h-9 w-9 rounded-full bg-[#9c7a4d]/10 flex items-center justify-center group-hover:bg-[#9c7a4d]/20 transition-colors">
                <Phone className="h-4 w-4 text-[#9c7a4d]" />
              </span>
              <span className="font-serif text-lg xl:text-xl font-bold text-[#0f172a] group-hover:text-[#9c7a4d] transition-colors whitespace-nowrap">{CONTACT.phone}</span>
            </a>
            <button
              onClick={openQuote}
              data-testid="nav-quote-btn"
              className="inline-flex items-center bg-[#9c7a4d] text-white hover:bg-[#0f172a] rounded-full px-7 py-3 text-xs font-bold uppercase tracking-[0.16em] transition-colors duration-300"
            >
              Get a Free Quote
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — Smartfix-style panel */}
      {open && (
        <div data-testid="nav-mobile-menu" data-lenis-prevent className="lg:hidden bg-white text-[#0f172a] max-h-[calc(100svh-200px)] overflow-y-auto overscroll-contain">
          {/* All services — prominent icon rows */}
          <div className="px-6 pt-6 pb-2" data-testid="nav-mobile-services-toggle">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.24em] text-[#9c7a4d] mb-4">All Services</p>
            <div className="flex flex-col">
              {CATEGORIES.map((c) => {
                const Icon = SERVICE_ICONS[c.id] || LayoutGrid;
                return (
                  <Link
                    key={c.id}
                    to={`/services/${c.id}`}
                    data-testid={`nav-mobile-service-${c.id}`}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between py-3.5 border-b border-[#f1f5f9] group"
                  >
                    <span className="flex items-center gap-4">
                      <Icon className="h-5 w-5 text-[#9c7a4d]" strokeWidth={1.8} />
                      <span className="text-base font-semibold text-[#0f172a]">{c.title}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-[#94a3b8] group-hover:text-[#9c7a4d] group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
              <Link to="/services" data-testid="nav-mobile-service-all" onClick={() => setOpen(false)} className="flex items-center gap-2 py-3.5 text-[0.7rem] font-bold uppercase tracking-[0.2em] text-[#9c7a4d]">
                <LayoutGrid className="h-4 w-4" /> View All Services
              </Link>
            </div>
          </div>

          {/* Get in touch card */}
          <div className="px-6 pb-10 pt-2">
            <div className="bg-[#0b1120] rounded-2xl p-7 text-center">
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.24em] text-[#c9a87c] mb-3">Get In Touch Now</p>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                onClick={() => trackCall("navbar_menu_card")}
                className="inline-flex items-center gap-3 text-white font-serif text-2xl sm:text-3xl"
              >
                <Phone className="h-6 w-6 text-[#c9a87c]" /> {CONTACT.phone}
              </a>
              <button
                data-testid="nav-mobile-quote-btn"
                onClick={() => { setOpen(false); openQuote(); }}
                className="mt-5 w-full inline-flex items-center justify-center bg-[#9c7a4d] text-white hover:bg-[#b8935c] rounded-full px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300"
              >
                Get a Free Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
}
